/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PayoutReportItem } from '../../gql-types.generated';
import { RootState } from '../../store';

interface SliceState {
  /** Calendar years this tenant has payout reports in, newest first. */
  years: number[];
  /** Reports for selectedYear only - never the whole history. */
  reports: PayoutReportItem[];
  /** The year the loaded reports belong to. Undefined until the first response lands. */
  selectedYear?: number;
  /**
   * True while a request is in flight. Not rendered as a spinner - the app bar's progress
   * bar covers that, driven by requestsInFlight - but the page still needs to know, so it
   * does not announce "No reports available" about data that has not arrived yet.
   */
  loading: boolean;
  error?: Error;
}

const initialState: SliceState = {
  years: [],
  reports: [],
  selectedYear: undefined,
  loading: false,
  error: undefined,
};

export const slice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    fetchPayoutReportsStart: state => {
      state.loading = true;
      state.error = undefined;
    },
    fetchPayoutReportsSuccess: (
      state,
      action: PayloadAction<{ years: number[]; reports: PayoutReportItem[]; selectedYear?: number }>,
    ) => {
      state.years = action.payload.years;
      state.reports = action.payload.reports;
      // The server resolves an omitted year to the newest one with data, and does not say
      // which it picked. The year list is that same answer in its first element, so the
      // selection is read from there rather than assumed to be the current calendar year.
      state.selectedYear = action.payload.selectedYear ?? action.payload.years[0];
      state.loading = false;
      state.error = undefined;
    },
    fetchPayoutReportsError: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearPayoutReports: () => initialState,
  },
});

export const selectPayoutReportYears = (state: RootState): number[] => state.reports.years;
export const selectPayoutReportItems = (state: RootState): PayoutReportItem[] => state.reports.reports;
export const selectSelectedPayoutReportYear = (state: RootState): number | undefined => state.reports.selectedYear;
export const selectPayoutReportsLoading = (state: RootState): boolean => state.reports.loading;
export const selectPayoutReportsError = (state: RootState): Error | undefined => state.reports.error;

export const { fetchPayoutReportsStart, fetchPayoutReportsSuccess, fetchPayoutReportsError, clearPayoutReports } = slice.actions;

export default slice.reducer;
